import type { IndicadorNaTela } from '../../../types/chamado'
import { fracaoPorExtenso, valorPorExtenso, valorPorExtensoPreciso } from '../../chamados/fiscalizacao/indicadores'
import { TRP } from './textoDoTrp'

/**
 * O CÁLCULO APLICADO de um indicador, como o termo assinado o escreve.
 *
 * O TRP nº 01 imprime "ICA = 1 – (3/45) = 93,33%" e "IAP = 25/36 = 69,44%". O número sozinho não é
 * conferível; a fração é — e é por ela que a contratada contesta.
 *
 * @param indicador o indicador apurado ou não
 * @returns a expressão, ou o motivo quando não houve apuração
 */
export function calculoAplicado(indicador: IndicadorNaTela): string {
  if (!indicador.apurado) {
    return indicador.motivoDaNaoApuracao ?? ''
  }
  const fracao = fracaoPorExtenso(indicador)
  const valor = valorPorExtenso(indicador.codigo, indicador.valor)
  if (fracao && valor) {
    return `${indicador.codigo} = ${fracao} = ${valor}`
  }
  if (valor) {
    return `${indicador.codigo} = ${valor}`
  }
  // O TIS não tem nota: ele soma um desconto por incidente. Escrever uma expressão de fração aqui
  // inventaria uma conta que o indicador não faz.
  return TRP.somaDeRedutores
}

/**
 * O NÍVEL APURADO — a célula que o modelo do TRP traz preenchida com texto de gabarito.
 *
 * **O que está errado no documento real.** A coluna "Nível" do quadro do item 11 do TRP nº 01 traz,
 * nas quatro linhas: "Até 1 dia útil de atraso", "Entre 1 e 3 dias úteis de atraso", "Superior a 3
 * dias úteis de atraso" e "Superior a 5 dias úteis de atraso". As quatro formam **uma única escada
 * crescente** — é o nível de UM indicador, copiado em quatro linhas. E não pode ser o de nenhum
 * deles: o ICA é uma razão de 0 a 1, o IAE é um percentual de aceite, e nenhum dos dois tem "dias
 * de atraso" como grandeza. Nem o TIS serve, porque ele mede em HORAS úteis, não em dias.
 *
 * **O que a célula deve trazer:** o índice apurado, na escala em que o contrato escreve as faixas
 * daquele indicador — que é o número que decidiu o desconto. Quando o arredondamento mudou o valor,
 * os dois aparecem: é o enquadramento que decide dinheiro, e escondê-lo faria a faixa parecer não
 * ter vindo do número ao lado.
 *
 * @param indicador o indicador apurado ou não
 * @returns o índice, ou o motivo quando não houve apuração
 */
export function nivelApurado(indicador: IndicadorNaTela): string {
  if (!indicador.apurado) {
    return indicador.motivoDaNaoApuracao ?? ''
  }
  const valor = valorPorExtenso(indicador.codigo, indicador.valor)
  if (!valor) {
    // O TIS é apurado sem nota. A célula traz o que ele de fato produziu — a soma —, e não um
    // índice inventado para preencher a coluna. Foi assim que o gabarito entrou no documento.
    return indicador.parcelas ? TRP.somaDoTis(indicador.parcelas.somaBruta, indicador.parcelas.tetoDoIndicador) : ''
  }
  // A comparação é entre os NÚMEROS, nunca entre os textos formatados: um ICA de 0,9451 e o seu
  // enquadramento 0,95 saem os dois como "0,95" em duas casas — e caem em faixas diferentes, 2% e
  // 0%. Comparar textos esconderia exatamente o caso em que o arredondamento custou dinheiro.
  if (indicador.valorEnquadrado == null || indicador.valorEnquadrado === indicador.valor) {
    return valor
  }
  const enquadrado = valorPorExtenso(indicador.codigo, indicador.valorEnquadrado)
  const apurado = valorPorExtensoPreciso(indicador.codigo, indicador.valor)
  return `${enquadrado} (apurado ${apurado})`
}

/**
 * A FAIXA ALCANÇADA, com o desconto que ela impõe.
 *
 * @param indicador o indicador apurado ou não
 * @returns o texto do contrato mais o percentual, ou vazio quando não houve apuração
 */
export function faixaAlcancada(indicador: IndicadorNaTela): string {
  if (!indicador.apurado) {
    return ''
  }
  const desconto = indicador.redutorPercentual === 0 ? 'sem desconto' : `${indicador.redutorPercentual}% de desconto`
  return indicador.faixaDoTr ? `${indicador.faixaDoTr} — ${desconto}` : desconto
}
